import React, { useEffect, useRef } from 'react';
import InterviewAudioCheck from '../ai-interview/components/InterviewAudioCheck';

/**
 * Pre-join lobby for the meeting. Configured device permissions are required before "Join":
 *   1. Camera on (live preview).
 *   2. Sharing the ENTIRE screen (not a window/tab) — validated in useScreenShare; a window/tab
 *      pick surfaces `shareWarning` and the candidate must re-share.
 * Mic permission is required when configured; speech testing remains optional.
 */
const Check = ({ done, children }) => (
  <li className="flex items-center gap-2.5 text-sm">
    <span className={`grid h-5 w-5 place-items-center rounded-full text-[11px] ${done ? 'bg-emerald-500 text-white' : 'bg-white/10 text-slate-400'}`}>
      {done ? '✓' : '•'}
    </span>
    <span className={done ? 'text-slate-200' : 'text-slate-400'}>{children}</span>
  </li>
);

const PreflightGate = ({
  camStream, cameraReady, cameraError, onEnableCamera,
  sharing, shareWarning, onShareScreen,
  requireCamera = true, requireScreen = true,
  requireMic = true,
  microphoneReady = false, onEnableMic, onAudioChecked,
  onJoin, joining,
}) => {
  const videoRef = useRef(null);
  const [audioTestSkipped, setAudioTestSkipped] = React.useState(false);
  useEffect(() => { if (videoRef.current && camStream) videoRef.current.srcObject = camStream; }, [camStream]);

  const canJoin = (!requireCamera || cameraReady) && (!requireScreen || sharing) && (!requireMic || microphoneReady);

  return (
    <div className="flex min-h-[100dvh] w-full items-center justify-center bg-[#0a0a12] px-4 py-8 text-white">
      <div className="w-full max-w-3xl">
        <div className="mb-6 text-center">
          <h1 className="bg-gradient-to-r from-primary-400 via-accent-400 to-secondary-400 bg-clip-text text-3xl font-extrabold text-transparent">
            WIZBOX Interview Suite
          </h1>
          <p className="mt-1 text-sm text-slate-400">Powered by Wiza · let's get your camera and screen ready before you join.</p>
        </div>

        <div className="grid gap-5 rounded-3xl bg-slate-900/70 p-5 ring-1 ring-white/10 sm:grid-cols-[1.2fr_1fr] sm:p-6">
          {/* camera preview */}
          <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-800 ring-1 ring-white/10">
            <video ref={videoRef} autoPlay playsInline muted
              className={`h-full w-full object-cover [transform:scaleX(-1)] ${cameraReady ? '' : 'hidden'}`} />
            {!cameraReady && (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-400">
                <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="6" width="13" height="12" rx="2" /><path d="M21 8l-5 4 5 4z" />
                </svg>
                <button type="button" onClick={onEnableCamera}
                  className="rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-violet-500">
                  Enable camera
                </button>
                {cameraError && <p className="px-6 text-center text-xs text-rose-400">{cameraError}</p>}
              </div>
            )}
            {cameraReady && (
              <span className="absolute bottom-2 left-2 rounded-md bg-black/55 px-2 py-1 text-xs font-medium backdrop-blur">You</span>
            )}
          </div>

          {/* checklist + actions */}
          <div className="flex flex-col">
            <p className="mb-3 text-sm font-semibold text-slate-200">Before you join</p>
            <ul className="space-y-2.5">
              {requireCamera && <Check done={cameraReady}>Camera is on</Check>}
              {requireScreen && <Check done={sharing}>Your <strong className="font-semibold">entire screen</strong> is shared</Check>}
              {requireMic && <Check done={microphoneReady}>Microphone connected</Check>}
            </ul>

            {requireScreen && !sharing && (
              <button type="button" onClick={onShareScreen}
                className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-white/5 px-4 py-2.5 text-sm font-medium text-slate-100 ring-1 ring-white/10 transition hover:bg-white/10">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M9 11l3-3 3 3M12 8v6" />
                </svg>
                Share entire screen
              </button>
            )}
            {sharing && (
              <p className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300 ring-1 ring-emerald-400/20">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Entire screen shared
              </p>
            )}
            {shareWarning && (
              <p className="mt-2 rounded-xl bg-amber-500/10 px-3 py-2 text-xs text-amber-300 ring-1 ring-amber-400/20">
                {shareWarning}
              </p>
            )}

            {requireMic && !microphoneReady && <button type="button" onClick={onEnableMic}
              className="mt-4 rounded bg-violet-600 px-3 py-2 text-xs font-semibold text-white">
              Enable microphone
            </button>}
            {requireMic && !audioTestSkipped && <div className="mt-4 text-slate-900">
              <InterviewAudioCheck disabled={!microphoneReady} onComplete={onAudioChecked} onSkip={() => setAudioTestSkipped(true)} />
            </div>}

            <button type="button" onClick={onJoin} disabled={!canJoin || joining}
              className="mt-auto w-full rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40">
              {joining ? 'Joining…' : canJoin ? 'Join interview' : 'Complete the steps above to join'}
            </button>
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-slate-500">
          Screen sharing is required for a fair, proctored interview. We hide your shared screen from your own view to avoid a mirror effect.
        </p>
      </div>
    </div>
  );
};

export default PreflightGate;
